import {
  Box,
  Button,
  Checkmark,
  Flex,
  Listbox,
  Popover,
  Tabs,
  Text,
  createListCollection,
  useListboxContext,
  useListboxItemContext,
} from "@chakra-ui/react-v3";
import { useEffect, useMemo, useState } from "react";
import { FiFilter, FiMinus, FiPlus } from "react-icons/fi";
import type { FilterToggle } from "../../shared-components/fhir-sections/fhir-section";

type TableFilterPopoverProps = {
  filters: FilterToggle[];
  activeFilters: FilterToggle[];
  setActiveFilters: (filters: FilterToggle[]) => void;
};

/**
 * Table filter component using Chakra UI v3 Popover + Listbox.
 * Features:
 * - All filters enabled by default
 * - Uncheck to hide types from results
 * - Immediate application (no Apply/Cancel buttons)
 * - Category parent checkboxes
 * - Shows "X types hidden" count
 */
export function TableFilterPopover({
  filters,
  activeFilters,
  setActiveFilters,
}: TableFilterPopoverProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const totalFilters = filters.length;
  const selectedCount = activeFilters.filter(f => f.isChecked).length;
  const hiddenCount = totalFilters - selectedCount;

  return (
    <Popover.Root
      open={isPopoverOpen}
      onInteractOutside={() => setIsPopoverOpen(false)}
      positioning={{ placement: "bottom-end" }}
    >
      <Popover.Trigger asChild>
        <Box position="relative">
          <Button
            size="sm"
            variant="outline"
            bg="bg.surface"
            color="fg.emphasized"
            borderWidth="1px"
            borderColor="border.emphasized"
            onClick={() => setIsPopoverOpen(!isPopoverOpen)}
            _hover={{ borderColor: "focusRing", bg: "bg.canvas" }}
          >
            <Flex align="center" gap={2}>
              <FiFilter />
              <Text>Filter</Text>
            </Flex>
          </Button>
          {hiddenCount > 0 && (
            <Box
              position="absolute"
              top="-6px"
              right="-6px"
              bg="red.500"
              color="white"
              borderRadius="full"
              minW="20px"
              h="20px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="xs"
              fontWeight="extrabold"
              px={1}
              boxShadow="md"
            >
              {hiddenCount}
            </Box>
          )}
        </Box>
      </Popover.Trigger>
      <Popover.Positioner zIndex={1500}>
        <Popover.Content
          width="400px"
          maxH="500px"
          bg="bg.surface"
          borderColor="border"
          borderWidth="1px"
          borderRadius="md"
          boxShadow="lg"
          p={0}
        >
          <FilterSelector
            filters={filters}
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
          />
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
}

type FilterSelectorProps = {
  filters: FilterToggle[];
  activeFilters: FilterToggle[];
  setActiveFilters: (filters: FilterToggle[]) => void;
};

type ListboxItem = {
  label: string;
  value: string;
};

/**
 * Filter selector using Chakra Tabs with separate Listbox for each group.
 * Each tab has its own "Select All" / "Deselect All" toggle.
 */
function FilterSelector({ filters, activeFilters, setActiveFilters }: FilterSelectorProps) {
  // Group filters by groupLabel
  const groupedFilters = useMemo(() => {
    const groups: Record<string, FilterToggle[]> = {};

    filters.forEach(filter => {
      const group = filter.groupLabel ?? "Other";
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group]?.push(filter);
    });

    return groups;
  }, [filters]);

  const groupNames = useMemo(() => Object.keys(groupedFilters), [groupedFilters]);

  const [activeTab, setActiveTab] = useState<string | undefined>(groupNames[0] ?? undefined);

  // Sync active tab with available groups
  useEffect(() => {
    if (!activeTab || !groupNames.includes(activeTab)) {
      setActiveTab(groupNames[0] ?? undefined);
    }
  }, [groupNames, activeTab]);

  return (
    <Box>
      <Tabs.Root
        value={activeTab ?? undefined}
        onValueChange={(details: { value: string }) => setActiveTab(details.value)}
        variant="enclosed"
      >
        <Tabs.List bg="transparent" borderBottomWidth="0">
          {groupNames.map(groupName => (
            <Tabs.Trigger key={groupName} value={groupName} cursor="pointer" fontSize="sm">
              {groupName}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        {groupNames.map(groupName => {
          const groupFilters = groupedFilters[groupName] ?? [];
          return (
            <Tabs.Content
              key={groupName}
              value={groupName}
              p={0}
              borderWidth="1px"
              borderColor="border.muted"
              borderTopWidth="0"
            >
              <FilterGroupContent
                groupName={groupName}
                groupFilters={groupFilters}
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
              />
            </Tabs.Content>
          );
        })}
      </Tabs.Root>
    </Box>
  );
}

type FilterGroupContentProps = {
  groupName: string;
  groupFilters: FilterToggle[];
  activeFilters: FilterToggle[];
  setActiveFilters: (filters: FilterToggle[]) => void;
};

/**
 * Content for a single filter group tab
 */
function FilterGroupContent({
  groupName,
  groupFilters,
  activeFilters,
  setActiveFilters,
}: FilterGroupContentProps) {
  const items: ListboxItem[] = groupFilters.map(filter => ({
    label: filter.label,
    value: `filter-${groupName}-${filter.label}`,
  }));

  const collection = createListCollection({ items });

  // Convert activeFilters to selected values for this group
  const currentGroupFilters = activeFilters
    .filter(f => f.isChecked && (f.groupLabel ?? "Other") === groupName)
    .map(f => `filter-${groupName}-${f.label}`);

  function handleValueChange(details: { value: string[] }) {
    const newSelectedValues = new Set(details.value);

    // Update filters based on selection
    const updatedFilters = activeFilters.map(filter => {
      if ((filter.groupLabel ?? "Other") !== groupName) {
        return filter;
      }
      const filterValue = `filter-${groupName}-${filter.label}`;
      return {
        ...filter,
        isChecked: newSelectedValues.has(filterValue),
      };
    });

    setActiveFilters(updatedFilters);
  }

  return (
    <Listbox.Root
      collection={collection}
      selectionMode="multiple"
      value={currentGroupFilters}
      onValueChange={handleValueChange}
      gap="0"
    >
      <Listbox.Content maxH="350px" overflowY="auto" bg="bg.surface">
        {groupFilters.map(filter => {
          const uniqueValue = `filter-${groupName}-${filter.label}`;
          return (
            <Listbox.Item
              item={{ label: filter.label, value: uniqueValue }}
              key={uniqueValue}
              cursor="pointer"
              bg="transparent"
              _hover={{ bg: "bg.muted" }}
              _selected={{ bg: "transparent" }}
            >
              <ListboxItemCheckmark />
              <Listbox.ItemText>{filter.label}</Listbox.ItemText>
            </Listbox.Item>
          );
        })}
      </Listbox.Content>
      <TabListboxFooter groupName={groupName} groupFilters={groupFilters} />
    </Listbox.Root>
  );
}

/**
 * Footer with "Select All" / "Deselect All" button for a specific tab/group
 */
function TabListboxFooter({
  groupName,
  groupFilters,
}: {
  groupName: string;
  groupFilters: FilterToggle[];
}) {
  const listbox = useListboxContext();
  const allValues = groupFilters.map(f => `filter-${groupName}-${f.label}`);
  const isAllSelected = allValues.every(val => listbox.value.includes(val));

  function handleToggleAll() {
    if (isAllSelected) {
      // Deselect all in this group
      const newValues = (listbox.value as string[]).filter(val => !allValues.includes(val));
      listbox.setValue(newValues);
    } else {
      // Select all in this group
      const newValues = [...new Set([...(listbox.value as string[]), ...allValues])];
      listbox.setValue(newValues);
    }
  }

  return (
    <Flex
      gap={2}
      p={3}
      borderTopWidth="1px"
      borderColor="border.muted"
      bg="bg.surface"
      justifyContent="flex-end"
      roundedBottom="md"
    >
      <Button
        size="sm"
        bg="btn.info.bg"
        color="btn.info.color"
        _hover={{ bg: "btn.info.hoverBg" }}
        onClick={handleToggleAll}
        cursor="pointer"
      >
        <Flex align="center" gap={2}>
          {isAllSelected ? <FiMinus /> : <FiPlus />}
          <Text>{isAllSelected ? "Deselect All" : "Select All"}</Text>
        </Flex>
      </Button>
    </Flex>
  );
}

/**
 * Checkmark for individual items
 */
function ListboxItemCheckmark() {
  const itemState = useListboxItemContext();
  return (
    <Checkmark
      filled
      size="sm"
      checked={itemState.selected}
      disabled={itemState.disabled}
      colorPalette="blue"
      cursor="pointer"
    />
  );
}
